/** Overview tab for the Customer Workspace (Demo Mode only). Editable customer summary, key
 * risks and next actions, structured as traditional form fields (bound to the parent workspace's
 * edit draft - see `../index.tsx`); key contacts stay read-only here since they're fully
 * editable from the Contacts tab. Rendered inside the single Section the parent workspace places
 * directly below its Tabs. */

import { Grid, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { CustomerRecord, RiskLevel } from '../../../../mocks/data/customerManagementData';
import ExternalSystemsSection from './ExternalSystemsSection';

interface OverviewTabProps {
  customer: CustomerRecord;
  onChange: (updater: (prev: CustomerRecord) => CustomerRecord) => void;
  /** Live (non-draft) copy of the same client, used by the External systems section - CRM
   * link/unlink/sync-toggle actions apply immediately rather than through the page's
   * Save/Cancel draft (see `ExternalSystemsSection`). */
  liveCustomer: CustomerRecord;
  onCrmUpdated: () => void;
}

const RISK_LEVELS: RiskLevel[] = ['Low', 'Medium', 'High'];

const OverviewTab = ({
  customer, onChange, liveCustomer, onCrmUpdated,
}: OverviewTabProps) => {
  const { t } = useTranslation('common');
  const keyContacts = customer.contacts.filter((contact) => contact.category === 'Client' || contact.category === 'Internal Owner');

  return (
    <Section style={{ p: 5 }}>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('customerOverviewSummary', 'Client summary')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewSector', 'Sector')}
                value={customer.sector}
                onChange={(e) => onChange((prev) => ({ ...prev, sector: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewOperatingModel', 'Operating model')}
                value={customer.operatingModel}
                onChange={(e) => onChange((prev) => ({ ...prev, operatingModel: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewOwner', 'Account owner')}
                value={customer.owner}
                onChange={(e) => onChange((prev) => ({ ...prev, owner: e.target.value }))}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewSites', 'Locations')}
                type="number"
                value={customer.sitesCount}
                onChange={(e) => onChange((prev) => ({ ...prev, sitesCount: Number(e.target.value) || 0 }))}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('customerOverviewKeyRisks', 'Key risks')}</Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}>
              <TextField
                select
                label={t('customerOverviewRiskLevel', 'Risk level')}
                value={customer.riskLevel}
                onChange={(e) => onChange((prev) => ({ ...prev, riskLevel: e.target.value as RiskLevel }))}
                fullWidth
                size="small"
              >
                {RISK_LEVELS.map((level) => (
                  <MenuItem key={level}
                    value={level}>{level}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewBlockers', 'Blockers')}
                helperText={t('customerOverviewBlockersHelper', 'One per line')}
                value={customer.blockers.join('\n')}
                onChange={(e) => onChange((prev) => ({ ...prev, blockers: e.target.value.split('\n').filter(Boolean) }))}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
            <Grid item
              xs={12}>
              <TextField
                label={t('customerOverviewMissingInfo', 'Missing information')}
                helperText={t('customerOverviewBlockersHelper', 'One per line')}
                value={customer.missingInfo.join('\n')}
                onChange={(e) => onChange((prev) => ({ ...prev, missingInfo: e.target.value.split('\n').filter(Boolean) }))}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('customerOverviewKeyContacts', 'Key contacts')}</Typography>
          {keyContacts.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('onboardingDetailNoContacts', 'No contacts captured yet.')}</Typography>
          ) : keyContacts.map((contact) => (
            <Typography key={contact.email}
              variant="textSM"
              display="block">{contact.name} - {contact.role} ({contact.category})</Typography>
          ))}
        </Grid>

        <Grid item
          xs={12}
          md={6}>
          <Typography variant="textSM"
            component="div"
            fontWeight={600}
            sx={{ mb: 2 }}>{t('customerOverviewNextActions', 'Next actions')}</Typography>
          <TextField
            value={customer.nextBestAction}
            onChange={(e) => onChange((prev) => ({ ...prev, nextBestAction: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
        </Grid>

        <Grid item
          xs={12}>
          <ExternalSystemsSection customer={liveCustomer}
            onUpdated={onCrmUpdated} />
        </Grid>
      </Grid>
    </Section>
  );
};

export default OverviewTab;
